<template>
	<v-container fluid class="pa-0">
		<v-container fluid>
			<v-card class="rounded-xl" elevation="0" max-width="2000">
				<v-card-text>
					<v-container fluid class="d-flex px-0">
						<v-btn
							class="mr-1"
							color="primary"
							depressed
							rounded
							@click="expand = !expand">
							<i class="fa-solid fa-bars" />
						</v-btn>
						<v-text-field
							v-model="search"
							dense
							rounded
							outlined
							hide-details
							label="Title Filter"
							clearable></v-text-field>
					</v-container>
				</v-card-text>
				<v-card-text
					:class="$vuetify.breakpoint.smAndDown ? '' : 'd-flex'">
					<!-- Desktop: side panel with horizontal expand -->
					<v-expand-x-transition v-if="$vuetify.breakpoint.mdAndUp">
						<filter-panel
							v-if="expand"
							class="rounded-xl mr-3"
							style="min-width: 280px; max-width: 280px"
							@handle-event="applyFilter"></filter-panel>
					</v-expand-x-transition>
					<!-- Mobile: stacked panel with vertical expand -->
					<v-expand-transition v-else>
						<filter-panel
							v-if="expand"
							class="rounded-xl mb-3"
							@handle-event="applyFilter"></filter-panel>
					</v-expand-transition>
					<div class="flex-grow-1">
						<v-container
							v-if="filteredProducts.length == 0"
							class="d-flex justify-center">
							<div>
								<div class="text-h5 font-weight-bold">
									Nothing to see
								</div>
								<div class="text-center text-h5">
									<v-icon>mdi-spider</v-icon>
								</div>
							</div>
						</v-container>
						<div
							v-for="(row, rowIndex) in productRows"
							:key="`row-${rowIndex}`">
							<v-row>
								<v-col
									v-for="product in row"
									:key="product.id"
									cols="12"
									sm="6"
									md="4"
									lg="3">
									<v-card
										class="rounded-xl pa-2"
										elevation="1"
										outlined
										:class="{
											primary:
												expandedProductId ===
												product.id,
										}">
										<product-card
											show-qty
											:elevation="0"
											:outlined="false"
											:product="product"
											:allow-add-to-cart="true" />
										<v-card-actions class="pa-0 pt-2">
											<v-btn
												text
												rounded
												block
												small
												:color="
													expandedProductId ===
													product.id
														? 'white'
														: 'primary'
												"
												@click="
													toggleExpand(product.id)
												">
												<v-icon large left>
													{{
														expandedProductId ===
														product.id
															? "mdi-chevron-up"
															: "mdi-chevron-down"
													}}
												</v-icon>
												{{
													expandedProductId ===
													product.id
														? "Show Less"
														: "Show More"
												}}
											</v-btn>
										</v-card-actions>
									</v-card>
								</v-col>
							</v-row>
							<!-- Expanded panel appears after the row containing the expanded product -->
							<v-expand-transition>
								<v-card
									v-if="getExpandedProductInRow(row)"
									class="rounded-xl mb-4 mt-2"
									elevation="1"
									outlined>
									<v-card-title
										class="d-flex justify-space-between flex-wrap">
										<div class="text-h6">
											{{
												getExpandedProductInRow(row)
													.title
											}}
											- Details
										</div>
										<div
											class="d-flex justify-end flex-wrap px-0"
											style="gap: 5px">
											<v-btn
												color="warning"
												depressed
												rounded
												small
												@click="
													$router.push({
														name: 'ProductUpdate',
														params: {
															id: getExpandedProductInRow(
																row,
															).id,
														},
													})
												">
												<v-icon left small>
													mdi-pencil
												</v-icon>
												Update
											</v-btn>
											<v-btn
												color="warning"
												depressed
												rounded
												small
												@click="
													addStock(
														getExpandedProductInRow(
															row,
														),
													)
												">
												<i
													class="fas fa-people-carry mr-2"></i>
												Add Stock
											</v-btn>
											<v-btn
												color="primary"
												depressed
												rounded
												small
												@click="
													getStocksByProductId(
														getExpandedProductInRow(
															row,
														).id,
													);
													getOrdersByProductId(
														getExpandedProductInRow(
															row,
														).id,
													);
													getProductTransactionsById(
														getExpandedProductInRow(
															row,
														).id,
													);
												">
												<v-icon left small>
													mdi-download
												</v-icon>
												Get Details
											</v-btn>
											<v-btn
												color="error"
												depressed
												rounded
												small
												:loading="
													getExpandedProductInRow(row)
														.id ==
													isDeleteLoadingIdx
												"
												@click="
													openDeleteDialog(
														getExpandedProductInRow(
															row,
														).id,
													)
												">
												<v-icon left small>
													mdi-close
												</v-icon>
												Delete
											</v-btn>
											<v-btn
												color="primary"
												rounded
												icon
												small
												:disabled="
													!(
														productIdToStocks[
															getExpandedProductInRow(
																row,
															).id
														] &&
														productIdToStocks[
															getExpandedProductInRow(
																row,
															).id
														].length
													)
												"
												@click="
													generateCsv(
														getExpandedProductInRow(
															row,
														).id,
													)
												">
												<v-icon small>
													mdi-file-download
												</v-icon>
											</v-btn>
										</div>
									</v-card-title>
									<v-card-text>
										<v-tabs
											v-model="tabs[expandedProductId]">
											<v-tab>Stocks</v-tab>
											<v-tab>Orders</v-tab>
											<v-tab>Transactions</v-tab>
										</v-tabs>
										<v-tabs-items
											v-model="tabs[expandedProductId]"
											touchless>
											<v-tab-item>
												<v-container
													class="d-flex justify-space-between">
													<div class="red--text">
														<h3>
															Average Stock Price
														</h3>
													</div>
													<div>
														<h3>
															${{
																findAvgFromStocks(
																	expandedProductId,
																)
															}}
														</h3>
													</div>
												</v-container>
												<v-simple-table height="250px">
													<thead>
														<tr>
															<th
																class="text-left">
																Id
															</th>
															<th
																class="text-left">
																Quantity
															</th>
															<th
																class="text-left">
																Stock Price
															</th>
															<th
																class="text-left">
																Vendor
															</th>
															<th
																class="text-left">
																Date Purchased
															</th>
															<th
																class="text-left">
																Created By
															</th>
															<th
																class="text-left">
																Date Created
															</th>
															<th
																class="text-left">
																Exp Date
															</th>
															<th>Action</th>
														</tr>
													</thead>
													<tbody>
														<tr
															v-for="stock in productIdToStocks[
																expandedProductId
															]"
															:key="`${expandedProductId}-stock-${stock.id}`">
															<td>
																{{ stock.id }}
															</td>
															<td>
																{{
																	stock.quantity
																}}
															</td>
															<td>
																${{
																	convertToCleanNumber(
																		stock.stockPrice,
																	)
																}}
															</td>
															<td>
																{{
																	getVendorName(
																		stock.vendorId,
																	)
																}}
															</td>
															<td>
																{{
																	convertToDate(
																		stock.datePurchased,
																		false,
																	)
																}}
															</td>
															<td>
																{{
																	getUserName(
																		stock.createdBy,
																	)
																}}
															</td>
															<td>
																{{
																	convertToDate(
																		stock.dateCreated,
																	)
																}}
															</td>
															<td>
																{{
																	convertToDate(
																		stock.expDate,
																		false,
																	)
																}}
															</td>
															<td>
																<v-btn
																	color="warning"
																	icon
																	depressed
																	@click="
																		updateStock(
																			getExpandedProductInRow(
																				row,
																			),
																			stock,
																		)
																	"
																	:disabled="
																		!stock.isActive
																	">
																	<i
																		class="fa-solid fa-gear"></i>
																</v-btn>
															</td>
														</tr>
													</tbody>
												</v-simple-table>
											</v-tab-item>
											<v-tab-item>
												<v-simple-table height="300px">
													<thead>
														<tr>
															<th
																class="text-left">
																Order Id
															</th>
															<th
																class="text-left">
																Quantity
															</th>
															<th
																class="text-left">
																Customer
															</th>
															<th
																class="text-left">
																Created By
															</th>
															<th
																class="text-left">
																Date Created
															</th>
															<th
																class="text-left">
																Invoice
															</th>
														</tr>
													</thead>
													<tbody>
														<tr
															v-for="order in productIdToOrders[
																expandedProductId
															]"
															:key="`${expandedProductId}-order-${order.id}`">
															<td>
																<a
																	@click="
																		$router.push(
																			{
																				name: 'Shipment.OrderDetail',
																				params: {
																					id: order.id,
																				},
																			},
																		)
																	">
																	{{
																		order.id
																	}}
																</a>
															</td>
															<td>
																{{
																	order
																		.orderDetails[0]
																		.quantity
																}}
															</td>
															<td>
																{{
																	getCustomerName(
																		order.customerId,
																	)
																}}
															</td>
															<td>
																{{
																	getUserName(
																		order.createdBy,
																	)
																}}
															</td>
															<td>
																{{
																	convertToDate(
																		order.dateCreated,
																	)
																}}
															</td>
															<td>
																<span
																	v-if="
																		order.invoiceId
																	">
																	<a
																		@click="
																			openInvoice(
																				order.invoiceId,
																			)
																		">
																		{{
																			order.invoiceId
																		}}
																	</a>
																</span>
															</td>
														</tr>
													</tbody>
												</v-simple-table>
											</v-tab-item>
											<v-tab-item>
												<v-simple-table height="250px">
													<thead>
														<tr>
															<th
																class="text-left">
																Id
															</th>
															<th
																class="text-left">
																Quantity
															</th>
															<th
																class="text-left">
																Unit Price
															</th>
															<th
																class="text-left">
																Account
															</th>
															<th
																class="text-left">
																Running Number
															</th>
															<th
																class="text-left">
																Created By
															</th>
															<th
																class="text-left">
																Date Created
															</th>
														</tr>
													</thead>
													<tbody>
														<tr
															v-for="transaction in productIdToTransactions[
																expandedProductId
															]"
															:key="`${expandedProductId}-transaction-${transaction.type}-${transaction.id}`">
															<td>
																<span>
																	{{
																		transaction.type
																	}}
																</span>
																<span
																	class="mx-1">
																	-
																</span>
																<span
																	v-if="
																		transaction.type ==
																		'Order'
																	">
																	{{
																		transaction.parentId
																	}}
																</span>
																<span v-else>
																	{{
																		transaction.id
																	}}
																</span>
															</td>
															<td>
																{{
																	transaction.quantity
																}}
															</td>
															<td>
																${{
																	convertToCleanNumber(
																		transaction.unitPrice,
																	)
																}}
															</td>
															<td>
																<span
																	v-if="
																		transaction.type ==
																		'Order'
																	">
																	{{
																		getCustomerName(
																			transaction.accountId,
																		)
																	}}
																</span>
																<span v-else>
																	{{
																		getVendorName(
																			transaction.accountId,
																		)
																	}}
																</span>
															</td>
															<td>
																{{
																	transaction.runningQuantity
																}}
															</td>
															<td>
																{{
																	getUserName(
																		transaction.createdBy,
																	)
																}}
															</td>
															<td>
																{{
																	convertToDate(
																		transaction.dateCreated,
																	)
																}}
															</td>
														</tr>
													</tbody>
												</v-simple-table>
											</v-tab-item>
										</v-tabs-items>
									</v-card-text>
								</v-card>
							</v-expand-transition>
						</div>
					</div>
				</v-card-text>
			</v-card>
		</v-container>
		<in-stock-dialog
			v-if="inStockDialog"
			v-model="inStockDialog"
			:p-product="selectedProduct"
			:p-stock="selectedStock"
			:is-updating="isUpdatingStock"
			@add="getStocksByProductId(selectedProduct.id)"
			@update="getStocksByProductId(selectedProduct.id)"
			@delete="getStocksByProductId(selectedProduct.id)"
			@set="(val) => (selectedStock = val)"></in-stock-dialog>
		<general-dialog
			v-model="deleteDialog"
			:options="deleteOptions"
			@handle-event="deleteProduct" />
		<v-overlay v-model="isLoading">
			<v-progress-circular indeterminate color="primary" />
		</v-overlay>
	</v-container>
</template>
<script>
import backendService from "@/services/backend-service";
import ProductCard from "@/components/ProductCard.vue";
import InStockDialog from "@/components/InStockDialog.vue";
import FilterPanel from "@/components/FilterPanel.vue";
import generalMixin from "@/js/general-mixin";
import GeneralDialog from "@/components/GeneralDialog.vue";
import eventBus from "@/js/event-bus";
import Decimal from "decimal.js";
import { OrderType } from "@/js/enum";
export default {
	name: "StorePage",
	components: {
		"product-card": ProductCard,
		"in-stock-dialog": InStockDialog,
		"filter-panel": FilterPanel,
		"general-dialog": GeneralDialog,
	},
	mixins: [generalMixin],
	data() {
		return {
			expand: false,
			expandedProductId: null,
			tmpStocks: [],
			productIdToStocks: {},
			productIdToOrders: {},
			productIdToTransactions: {},
			selectedProduct: {},
			selectedStock: {},
			products: [],
			store: {},
			isLoadingIdx: -1,
			isDeleteLoadingIdx: -1,
			isLoading: false,
			isUpdatingStock: false,
			inStockDialog: false,
			toDeleteProduct: -1,
			deleteDialog: false,
			deleteOptions: {
				btnText: "Yes",
				message: "This action is not revertable!",
				title: "Are you sure you want to delete this product?",
			},
		};
	},
	computed: {
		tabs: {
			get() {
				return this.$store.getters["interactStore/productTabs"];
			},
			set(val) {
				this.$store.commit("interactStore/setProductTabs", val);
			},
		},
		cart() {
			return this.$store.getters["cart"];
		},
		cartCount() {
			return this.$store.getters["cartCount"];
		},
		filteredProducts() {
			let products = this.products;
			let searchStr = this.search;
			searchStr = searchStr ? searchStr.trim().toLowerCase() : "";
			if (searchStr && searchStr.length) {
				products = products.filter((e) => {
					let str = e.title;
					return str
						.toString()
						.trim()
						.toLowerCase()
						.includes(searchStr.toLowerCase());
				});
			}
			return products;
		},
		columnsPerRow() {
			if (this.$vuetify.breakpoint.lgAndUp) return 4;
			if (this.$vuetify.breakpoint.md) return 3;
			if (this.$vuetify.breakpoint.sm) return 2;
			return 1;
		},
		productRows() {
			const rows = [];
			const cols = this.columnsPerRow;
			for (let i = 0; i < this.filteredProducts.length; i += cols) {
				rows.push(this.filteredProducts.slice(i, i + cols));
			}
			return rows;
		},
		Regular() {
			return this.getIdByEnum(OrderType.Regular);
		},
		search: {
			get() {
				return this.$store.getters["search"];
			},
			set(val) {
				this.$store.commit("setSearch", val);
			},
		},
	},
	watch: {
		"$route.query": function () {
			this.getProducts(this.$route.query);
		},
	},
	mounted: function () {
		let query = this.$route.query;
		this.store.id = this.$route.params.id;
		this.getProducts(this.$route.query);
		this.titleFilter = query.titleFilter;
	},
	methods: {
		toggleExpand(productId) {
			if (this.expandedProductId === productId) {
				this.expandedProductId = null;
			} else {
				this.expandedProductId = productId;
			}
		},
		getExpandedProductInRow(row) {
			return row.find((p) => p.id === this.expandedProductId);
		},
		addStock(product) {
			this.selectedProduct = product;
			this.selectedStock = {};
			this.inStockDialog = true;
			this.isUpdatingStock = false;
		},
		applyFilter(filter) {
			this.getProducts(filter);
		},
		findAvgFromStocks(productId) {
			if (
				productId &&
				this.productIdToStocks[productId] &&
				this.productIdToStocks[productId].length
			) {
				let stocks = this.productIdToStocks[productId];
				let sum = new Decimal(0);
				let count = 0;

				for (let i = 0; i < stocks.length; i++) {
					let stock = stocks[i];
					let subtotal = Decimal.mul(
						stock.stockPrice,
						stock.quantity,
					);
					sum = sum.plus(subtotal);
					count += stock.quantity;
				}
				return sum.dividedBy(new Decimal(count)).toFixed(2);
			}
		},
		generateCsv(productId) {
			const stocks = this.productIdToStocks[productId];
			const orders = this.productIdToOrders[productId];
			const product = this.products.find((e) => e.id == productId);
			let headerStr =
				"Stock Id;Quantity;Stock Price;Date Purchased;Created By;Date Created;Exp Date";
			let csvContent = `${product.id};${product.title}\r\n`;
			csvContent += headerStr + "\r\n";
			for (let i = 0; i < stocks.length; i++) {
				const stock = stocks[i];
				let rowStr = `${stock.id};${parseInt(
					stock.quantity,
				)};${this.convertToCleanNumber(
					stock.stockPrice,
				)};${this.convertToDate(stock.datePurchased)};${
					stock.createdBy.first
				} ${stock.createdBy.last};${this.convertToDate(
					stock.dateCreated,
				)};${this.convertToDate(stock.expDate, false)}`;
				csvContent += rowStr + "\r\n";
			}
			csvContent += "\r\n";
			headerStr = "Order Id;Quantity;Date Created;Invoice Id";
			csvContent += headerStr + "\r\n";
			for (let i = 0; i < orders.length; i++) {
				const order = orders[i];
				let rowStr = `${order.id};${parseInt(
					order.orderDetails[0].quantity,
				)};${this.convertToDate(order.dateCreated)};${order.invoiceId}`;
				csvContent += rowStr + "\r\n";
			}
			let blob = new Blob([csvContent], { type: "text/plain" });
			let url = URL.createObjectURL(blob);
			let link = document.createElement("a");
			link.setAttribute("href", url);
			link.setAttribute("download", `stock-${product.title}.csv`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		},
		getProducts(filter) {
			this.isLoading = true;
			backendService
				.getProducts(filter)
				.then((response) => {
					this.productIdToStocks = {};
					this.timer(1000).then(() => {
						this.isLoading = false;
						this.products = response.data;
						this.products.forEach((product) => {
							this.productIdToStocks[product.id] = [];
							this.productIdToOrders[product.id] = [];
						});
					});
				})
				.catch(() => {
					this.productIdToStocks = {};
					this.timer(1000).then(() => {
						this.isLoading = false;
						this.products = [];
					});
				});
		},
		getStocksByProductId(productId) {
			backendService.getStocksByProductId(productId).then((response) => {
				this.productIdToStocks[productId] = response.data;
				this.$forceUpdate();
			});
		},
		getOrdersByProductId(productId) {
			backendService.getOrdersByProductId(productId).then((response) => {
				this.productIdToOrders[productId] = response.data.filter(
					(e) => e.typeId == this.Regular,
				);
				this.$forceUpdate();
			});
		},
		getProductTransactionsById(id) {
			backendService.getProductTransactionsById(id).then((response) => {
				this.productIdToTransactions[id] = response.data;
				this.$forceUpdate();
			});
		},
		openDeleteDialog(productId) {
			this.toDeleteProduct = productId;
			this.deleteDialog = true;
		},
		deleteProduct() {
			this.deleteDialog = false;
			this.isDeleteLoadingIdx = this.toDeleteProduct;
			backendService
				.deleteProductById(this.toDeleteProduct)
				.then(() => {
					this.timer(1000).then(() => {
						this.products = this.products.filter(
							(x) => x.id != this.toDeleteProduct,
						);
						this.toDeleteProduct = -1;
						this.isDeleteLoadingIdx = -1;
						this.expandedProductId = null;
						eventBus.$emit(
							"setSnackbar",
							"Successfully deleted the product",
							"success",
							true,
						);
					});
				})
				.catch(() => {
					this.timer(1000).then(() => {
						this.isDeleteLoadingIdx = -1;
						this.toDeleteProduct = -1;
						eventBus.$emit(
							"setSnackbar",
							"Ops!, something is not right!",
							"error",
							true,
						);
					});
				});
		},
		openInvoice(invoiceId) {
			let routeData = this.$router.resolve({
				name: "InvoiceUpdate",
				params: { id: invoiceId },
			});
			window.open(routeData.href, "_blank");
		},
		updateStock(product, stock) {
			this.selectedProduct = product;
			this.selectedStock = stock;
			this.inStockDialog = !this.inStockDialog;
			this.isUpdatingStock = true;
		},
	},
};
</script>
